import React from "react";
import { FormikValues } from "formik";
import { object, string } from "yup";
import { ModalV2 } from "@jsq/ds/core";
import { Form, SubmitButton, CancelButton } from "@jsq/ds/forms";
import { Tab } from "../../types";
import * as styles from "../styles_vanilla.css";
import ModalBodyContent from "./ModalBodyContent";

interface Props<T extends FormikValues = FormikValues> {
  show: boolean;
  onHide: () => void;
  title: string;
  submitButtonText: string;
  submitButtonTestId: string;
  isSubmitting: boolean;
  initialValues: T;
  onSubmit: (values: Partial<T>) => Promise<void>;
  isEditMode?: boolean;
  tabs?: Tab[];
}

const validationSchema = object().shape({
  name: string()
    .required("Section name is required")
    .min(1, "Section name cannot be empty")
    .max(44, "Section name cannot exceed 44 characters")
    .test(
      "no-whitespace-only",
      "Section name cannot contain only whitespace",
      function (value) {
        if (value && value.trim() === "") {
          return false;
        }
        return true;
      },
    ),
  description: string().max(100, "Description cannot exceed 100 characters").optional(),
  tabName: string()
    .max(50, "Tab name cannot exceed 50 characters")
    .test("not-other", "Tab name cannot be 'other'", function (value) {
      if (value && value.toLowerCase() === "other") {
        return false;
      }
      return true;
    })
    .optional(),
});

const SectionModal = <T extends FormikValues = FormikValues>({
  show,
  onHide,
  title,
  submitButtonText,
  submitButtonTestId,
  isSubmitting,
  initialValues,
  onSubmit,
  isEditMode = false,
  tabs = [],
}: Props<T>) => {
  return (
    <ModalV2 show={show} onHide={onHide} title={title}>
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <ModalV2.Body>
          <ModalBodyContent isEditMode={isEditMode} tabs={tabs} />
        </ModalV2.Body>

        <ModalV2.Footer>
          <div className={styles.modalFooter}>
            <CancelButton onClick={onHide} disabled={isSubmitting}>
              Cancel
            </CancelButton>
            <SubmitButton isLoading={isSubmitting} data-testid={submitButtonTestId}>
              {submitButtonText}
            </SubmitButton>
          </div>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};

export default SectionModal;
